class GosNumberFormatter {
  constructor() {
    // Разрешенные кириллические буквы для гос номеров
    this.allowedLetters = 'АВЕКМНОРСТУХ'

    // Регулярное выражение для валидации стандартного формата
    this.standardPattern = /^[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}(\d{2,3})?$/
  }

  /**
   * Форматирует введенный текст согласно маске стандартного гос номера
   * @param {string} value - Введенное значение
   * @returns {string} Отформатированное значение
   */
  format(value) {
    if (!value) return ''

    // Удаляем все лишние символы и приводим к верхнему регистру
    const cleaned = this.cleanInput(value)

    // Форматируем только стандартный номер
    return this.formatStandard(cleaned)
  }

  /**
   * Очищает ввод от недопустимых символов
   * @param {string} value - Исходное значение
   * @returns {string} Очищенное значение
   */
  cleanInput(value) {
    return value
      .toUpperCase()
      .replace(/[^АВЕКМНОРСТУХA-Z0-9]/g, '') // Удаляем все кроме разрешенных символов
      .replace(/[A-Z]/g, (match) => this.convertLatinToCyrillic(match)) // Конвертируем латиницу в кириллицу
  }

  /**
   * Конвертирует латинские буквы в кириллические аналоги
   * @param {string} letter - Латинская буква
   * @returns {string} Кириллическая буква
   */
  convertLatinToCyrillic(letter) {
    const mapping = {
      A: 'А',
      B: 'В',
      C: 'С',
      E: 'Е',
      H: 'Н',
      K: 'К',
      M: 'М',
      O: 'О',
      P: 'Р',
      T: 'Т',
      X: 'Х',
      Y: 'У',
    }
    return mapping[letter] || letter
  }

  /**
   * Форматирует стандартный номер (А123ВЕ77)
   * @param {string} value - Очищенное значение
   * @returns {string} Отформатированный номер
   */
  formatStandard(value) {
    let result = ''
    let letterCount = 0
    let digitCount = 0
    let regionStarted = false

    for (let i = 0; i < value.length && result.length < 10; i++) {
      const char = value[i]

      if (result.length === 0) {
        // Первый символ должен быть буквой
        if (this.allowedLetters.includes(char)) {
          result += char
          letterCount++
        }
      } else if (result.length >= 1 && result.length <= 3) {
        // Позиции 2-4: цифры
        if (/\d/.test(char) && digitCount < 3) {
          result += char
          digitCount++
        }
      } else if (result.length >= 4 && result.length <= 5) {
        // Позиции 5-6: буквы
        if (this.allowedLetters.includes(char) && letterCount < 3) {
          result += char
          letterCount++
        }
      } else if (result.length >= 6) {
        // Позиции 7-9: цифры региона (опционально)
        if (/\d/.test(char)) {
          if (!regionStarted) {
            result += ' ' + char
            regionStarted = true
          } else {
            result += char
          }
        }
      }
    }

    return result
  }

  /**
   * Валидирует гос номер
   * @param {string} value - Значение для валидации
   * @returns {boolean} Результат валидации
   */
  validate(value) {
    if (!value) return true // Пустое значение допустимо

    const cleaned = this.cleanInput(value)

    return this.standardPattern.test(cleaned) || this.isPartialValid(cleaned)
  }

  /**
   * Проверяет, является ли частично введенный номер валидным
   * @param {string} value - Значение для проверки
   * @returns {boolean}
   */
  isPartialValid(value) {
    if (value.length === 0) return true

    const firstChar = value[0]

    // Первый символ должен быть разрешенной буквой
    if (!this.allowedLetters.includes(firstChar)) {
      return false
    }

    // Проверяем стандартный формат поэтапно
    const withoutSpaces = value.replace(/\s/g, '')
    if (withoutSpaces.length <= 9) {
      const letterPart1 = withoutSpaces.slice(0, 1)
      const digitPart1 = withoutSpaces.slice(1, 4)
      const letterPart2 = withoutSpaces.slice(4, 6)
      const digitPart2 = withoutSpaces.slice(6)

      // Проверяем каждую часть
      const isFirstLetterValid = this.allowedLetters.includes(letterPart1)
      const isFirstDigitPartValid = /^\d{0,3}$/.test(digitPart1)
      const isSecondLetterPartValid = /^[АВЕКМНОРСТУХ]{0,2}$/.test(letterPart2)
      const isSecondDigitPartValid = /^\d{0,3}$/.test(digitPart2)

      return (
        isFirstLetterValid &&
        isFirstDigitPartValid &&
        isSecondLetterPartValid &&
        isSecondDigitPartValid
      )
    }

    return false
  }

  /**
   * Проверяет, является ли номер полным и корректным
   * @param {string} value - Значение для проверки
   * @returns {boolean}
   */
  isCompleteNumber(value) {
    if (!value || value.trim() === '') return true // Пустое значение допустимо

    const cleaned = this.cleanInput(value)

    // Стандартный формат: А123ВЕ (минимум 6 символов) или А123ВЕ77/А123ВЕ777
    if (this.allowedLetters.includes(cleaned[0])) {
      // Проверяем на стандартный формат (минимум 6 символов)
      if (cleaned.length >= 6) {
        const letterPart1 = cleaned.slice(0, 1)
        const digitPart1 = cleaned.slice(1, 4)
        const letterPart2 = cleaned.slice(4, 6)
        const digitPart2 = cleaned.slice(6)

        const isBasicValid =
          this.allowedLetters.includes(letterPart1) &&
          /^\d{3}$/.test(digitPart1) &&
          /^[АВЕКМНОРСТУХ]{2}$/.test(letterPart2)

        if (!isBasicValid) return false

        // Если есть цифры региона, они должны быть 2 или 3 цифры
        if (digitPart2.length > 0) {
          return /^\d{2,3}$/.test(digitPart2)
        }

        return true // А123ВЕ без региона - валидно
      }
      return false
    }

    return false
  }

  /**
   * Получает позицию курсора после форматирования
   * @param {string} oldValue - Старое значение
   * @param {string} newValue - Новое значение
   * @param {number} oldCursor - Старая позиция курсора
   * @returns {number} Новая позиция курсора
   */
  getCursorPosition(oldValue, newValue, oldCursor) {
    // Простая логика: если добавился пробел, сдвигаем курсор
    if (newValue.length > oldValue.length && newValue.includes(' ')) {
      const spaceIndex = newValue.indexOf(' ')
      if (oldCursor >= spaceIndex) {
        return oldCursor + 1
      }
    }
    return Math.min(oldCursor, newValue.length)
  }
}

export default ({ app }, inject) => {
  const gosNumberFormatter = new GosNumberFormatter()
  inject('gosNumberFormatter', gosNumberFormatter)
}
